import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Box, Tabs } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { ActiveJobsLimitWarning } from 'modules/ats/components/ActiveJobsLimitWarning'
import { UploadCvsWidget, useCvBulkUpload } from 'modules/ats/components/UploadCvsWidget'
import { useAtsContext } from 'modules/ats/lib/atsContext'
import { hasJobBoards } from 'modules/ats/pages/JobPostingPromotion/utils'
import type Job from 'modules/ats/store/models/Job'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

interface Props {
  job: Job
  children: React.ReactElement
  isReviewerRole?: boolean
}

export const JobPostingLayout = observer(({ job, children, isReviewerRole = false }: Props) => {
  const { pathname } = useLocation()
  const { navigation } = useNavigation()
  const tabs = useTabsForJob(job.id, job.isPublished, isReviewerRole)

  const { cvBulkUpload } = useCvBulkUpload(job.id)
  const navigate = useNavigate()

  return (
    <ResponsivePage.Body
      variant="full"
      showHeader={false}
      actions={{
        primary: [
          {
            to: navigation.settings.ats.fullPath(),
            label: i18n.t('ats.jobs.settings'),
            icon: 'settings',
            variant: 'secondary',
          },
        ],
      }}
    >
      <Box gap="s32">{cvBulkUpload && <UploadCvsWidget cvBulkUpload={cvBulkUpload} />}</Box>

      <Tabs currentId={pathname} onTabClicked={(tabId) => navigate(tabId)}>
        {tabs.map((tab) => (
          <Tabs.Tab key={tab.href} id={tab.href} label={tab.label} />
        ))}
      </Tabs>

      <Box paddingTop="s32">
        <ActiveJobsLimitWarning />

        {React.cloneElement(React.Children.only(children), { job })}
      </Box>
    </ResponsivePage.Body>
  )
})

function useTabsForJob(id: number, isPublished: boolean, isReviewerRole: boolean) {
  const { joinEnabled, hasPremiumPortals, referralsEnabled } = useAtsContext()
  const { navigation } = useNavigation()

  const {
    applications: applicationsRoute,
    promotion: promotionRoute,
    standardPortals: standardPortalsRoute,
    premiumPortals: premiumPortalsRoute,
  } = navigation.ats.jobs.jobsId

  const params = { jobId: id }

  const tabs = [
    {
      label: i18n.t('ats.job_details.tab_headers.applications'),
      href: applicationsRoute.fullPath(params),
    },
  ]

  if ((hasJobBoards(isPublished) || referralsEnabled) && !isReviewerRole) {
    tabs.push({
      label: i18n.t('ats.job_details.tab_headers.promotion'),
      href: promotionRoute.fullPath(params),
    })
  }

  if (joinEnabled) {
    tabs.push({
      label: i18n.t('ats.job_details.tab_headers.standard_portals'),
      href: standardPortalsRoute.fullPath(params),
    })
  }

  if (hasPremiumPortals) {
    tabs.push({
      label: i18n.t('ats.job_details.tab_headers.premium_portals'),
      href: premiumPortalsRoute.fullPath(params),
    })
  }

  return tabs
}
